import React, { useState } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

const CustomOption = ({ innerRef, innerProps, isFocused, data }) => (
  <div
    ref={innerRef}
    {...innerProps}
    style={{
      backgroundColor: isFocused ? '#d5e5fa' : 'white',
      padding: 10,
      cursor: 'pointer',
    }}
  >
    <h4 className="mb-1">{data.label}</h4>
    <p className="small text-muted mb-0">{data.summary}</p>
  </div>
);

CustomOption.propTypes = {
  innerRef: PropTypes.any,
  innerProps: PropTypes.any,
  isFocused: PropTypes.any,
  isSelected: PropTypes.any,
  data: PropTypes.any,
};

const QuickSearch = () => {
  const history = useHistory();
  const [selectedOption, setSelectedOption] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const permissions = useSelector((state) => state.permissionsReducer);
  const features = useSelector((state) => state.featuresReducer);
  const HD_PAGES_LIST = [
    {
      id: 1,
      title: 'Dashboard',
      summary: 'Track your projects, tasks, and recent activities at a glance',
      route: '/dashboard',
      isEnabled: permissions && permissions.includes('dashboard_view'),
    },
    {
      id: 2,
      title: 'Track',
      summary:
        'Log and manage time efficiently with direct entries in calendar view',
      route: '/track',
      isEnabled: true,
    },
    {
      id: 3,
      title: 'Timeline',
      summary: 'Visualize task progress and deadlines with a timeline view',
      route: '/timeline',
      isEnabled: permissions && permissions.includes('timeline_view'),
    },
    {
      id: 4,
      title: 'Clients',
      summary: 'Streamline client management with essential details',
      route: '/clients',
      isEnabled: permissions && permissions.includes('client_manage'),
    },
    {
      id: 5,
      title: 'Active Projects',
      summary:
        'Detailed list of active projects featuring total hours logged, tasks, and members',
      route: '/projects/active',
      isEnabled: permissions && permissions.includes('project_view_own'),
    },
    {
      id: 6,
      title: 'Archived Projects',
      summary: 'Browse archived projects with options to restore or delete',
      route: '/projects/archived',
      isEnabled: permissions && permissions.includes('project_archive'),
    },
    {
      id: 7,
      title: 'Active Team Members',
      summary:
        'Manage active team members, view their hours, track task progress, and adjust roles all in one place',
      route: '/team/active',
      isEnabled: permissions && permissions.includes('team_member_view'),
    },
    {
      id: 8,
      title: 'Inactive Team Members',
      summary:
        'Manage inactive team members with options to reactivate or delete',
      route: '/team/inactive',
      isEnabled: permissions && permissions.includes('team_member_deactivate'),
    },
    {
      id: 9,
      title: 'Team Groups',
      summary: 'Organize team members into groups',
      route: '/team/groups',
      isEnabled: permissions && permissions.includes('team_group_manage'),
    },
    {
      id: 10,
      title: 'Detailed Timesheet Report',
      summary:
        'Generate detailed timesheet reports to analyze work hours and productivity',
      route: '/timesheet/detailed',
      isEnabled: permissions && permissions.includes('timesheet_view_detailed'),
    },
    {
      id: 11,
      title: 'Summarized Timesheet Report',
      summary:
        'Access summarized timesheet reports for a quick overview of team productivity',
      route: '/timesheet/summarized',
      isEnabled:
        permissions && permissions.includes('timesheet_view_summarized'),
    },
    {
      id: 12,
      title: 'Member Activity',
      summary:
        'Review detailed work session logs to track member activity and engagement',
      route: '/activity/member',
      isEnabled: permissions && permissions.includes('activity_member_view'),
    },
    {
      id: 13,
      title: 'Team Activity',
      summary: 'Analyze overall team activity and productivity',
      route: '/activity/team/date-range',
      isEnabled: permissions && permissions.includes('activity_team_view'),
    },
    {
      id: 14,
      title: 'Overtime Report',
      summary: 'To track and analyze overtime hours across your team',
      route: '/reports/overtime',
      isEnabled:
        features &&
        features.isOverTimeEnabled &&
        permissions &&
        permissions.includes('report_overtime'),
    },
    {
      id: 15,
      title: 'Screenshot Report',
      summary: 'Review and analyze screenshots to monitor activity',
      route: '/reports/screenshot',
      isEnabled: permissions && permissions.includes('screenshot_view'),
    },
    {
      id: 16,
      title: 'Worksession Chart',
      summary: 'Explore worksession data through chart reports',
      route: '/reports/charts',
      isEnabled: permissions && permissions.includes('work_session_view_chart'),
    },
    {
      id: 17,
      title: 'Performance Chart',
      summary: 'Explore performance data through chart reports',
      route: '/reports/charts',
      isEnabled: permissions && permissions.includes('performance_view_chart'),
    },
    {
      id: 18,
      title: 'Invoices',
      summary: 'Manage and generate client invoices',
      route: '/invoices',
      isEnabled:
        features &&
        features.isInvoiceEnabled &&
        permissions &&
        permissions.includes('invoice_manage'),
    },
    {
      id: 19,
      title: 'Settings > General',
      summary: 'Configure preferences from the General Settings page',
      route: '/settings/general',
      isEnabled: permissions && permissions.includes('configuration_manage'),
    },
    {
      id: 20,
      title: 'Settings > Features',
      summary:
        'Adjust functionality and access with customizable options in Feature Settings',
      route: '/settings/general',
      isEnabled: permissions && permissions.includes('configuration_manage'),
    },
    {
      id: 21,
      title: 'Settings > Profile',
      summary: 'Update and manage your personal profile settings',
      route: '/settings/profile',
      isEnabled: true,
    },
    {
      id: 22,
      title: 'Settings > Password',
      summary: 'Secure your account by updating your password',
      route: '/settings/password',
      isEnabled: true,
    },
    {
      id: 23,
      title: 'Billing',
      summary: 'Control and update your billing information',
      route: '/settings/billing',
      isEnabled: permissions && permissions.includes('subscription_manage'),
    },
    {
      id: 24,
      title: 'Settings > Teams',
      summary:
        'View team members and roles, and manage daily work session limit',
      route: '/settings/teams',
      isEnabled: permissions && permissions.includes('team_member_view'),
    },
    {
      id: 25,
      title: 'Email Summaries',
      summary:
        'Subscribe to email summaries with options to select daily, weekly, or bimonthly reports',
      route: '/settings/email-summaries',
      isEnabled: permissions && permissions.includes('email_summaries_manage'),
    },
    {
      id: 26,
      title: 'Integrations',
      summary: 'Explore and manage third-party integrations',
      route: '/settings/integration-status',
      isEnabled: permissions && permissions.includes('integration_manage'),
    },
    {
      id: 27,
      title: 'Timesheet / Timecard View',
      summary: 'View and track your daily work sessions on a weekly basis',
      route: '/timesheet/view',
      isEnabled: features && features.isTimesheetApproval,
    },
    {
      id: 28,
      title: 'Timesheet / Timecard Approval',
      summary: 'Review and approve submitted timesheet',
      route: '/timesheet/approval',
      isEnabled:
        features &&
        features.isTimesheetApproval &&
        permissions &&
        permissions.includes('timesheet_approval'),
    },
    {
      id: 29,
      title: 'Schedules > Shifts',
      summary: 'Plan and manage shifts for team members',
      route: '/schedules/shifts',
      isEnabled: features && features.isSchedulingEnabled,
    },
    {
      id: 30,
      title: 'Schedules > Holidays',
      summary: 'View and manage holiday schedules',
      route: '/schedules/holidays',
      isEnabled: features && features.isSchedulingEnabled,
    },
    {
      id: 31,
      title: 'Reports > Shrinkage',
      summary:
        'To highlight discrepancies between scheduled and actual worked hours for better efficiency',
      route: '/reports/shrinkage',
      isEnabled:
        features &&
        features.isSchedulingEnabled &&
        permissions &&
        permissions.includes('shrinkage_view'),
    },
    {
      id: 32,
      title: 'Reports > Attendance',
      summary: 'To monitor and analyze team presence and punctuality',
      route: '/reports/attendance',
      isEnabled:
        features &&
        features.isSchedulingEnabled &&
        permissions &&
        permissions.includes('attendance_view'),
    },
    {
      id: 33,
      title: 'Time Off',
      summary:
        'Create and manage time-off policies and types to requesting leaves',
      route: '/timeoff/policy',
      isEnabled: permissions && permissions.includes('time_off_settings'),
    },
    {
      id: 34,
      title: 'Leave Policy',
      summary: 'Create and manage time-off policies',
      route: '/timeoff/policy',
      isEnabled: permissions && permissions.includes('time_off_settings'),
    },
  ];

  // Prepare the options in the format that react-select expects
  const qsOptions = HD_PAGES_LIST.map((page) => {
    const QS_OPTION = {
      value: page.id,
      label: page.title,
      route: page.route,
      summary: page.summary,
      isEnabled: page.isEnabled,
    };
    if (page.isEnabled) {
      return QS_OPTION;
    }
    return null;
  });

  // Dynamically set options based on input value
  const filteredOptions = inputValue
    ? qsOptions.filter(
        (option) =>
          option !== null &&
          option.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : [];

  const handleChange = (option) => {
    setSelectedOption(option);
    history.push(option.route);
  };

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    return newValue;
  };

  const customComponents = {
    Option: CustomOption,
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '48px',
      height: '48px',
      fontSize: '16px',
      padding: '0 8px',
    }),
    // To remove the dropdown indicator (arrow)
    dropdownIndicator: (base) => ({
      ...base,
      display: 'none',
    }),
    // To remove the separator line
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none',
    }),
  };

  const getNoOptionsMessage = () => (inputValue ? 'No pages found' : null);

  return (
    <Select
      className="quick-search-field"
      value={selectedOption}
      styles={customStyles}
      onChange={handleChange}
      options={filteredOptions}
      components={customComponents}
      onInputChange={handleInputChange}
      placeholder="Quick Search"
      isClearable
      noOptionsMessage={getNoOptionsMessage}
    />
  );
};

export default QuickSearch;
